<!--@format-->
<template>
    <div class="vendor-solution-publish-widget">
        <div class="widget-header">
            <h3>Publish to Partners</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <p>Please select which partners you would like this solution to be published to:</p>
            <selector-table
                v-model="selectedClients"
                hide-headers
                item-container-title="Selected Partners"
                is-chip-display
                is-multiselect
                :is-initializing="isLoading"
                :items="availableClients"
                :selected-items="selectedClients"
                :table-config="tableConfig"
                @removed-item="handleRemovedItem"
                @selected-item="handleSelectedItem"
                @toggle-select-all="handleToggleSelectAllItems"
                @query="handleQuery"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <app-button :loading="isLoading" @click="save">
                    {{ isLoading ? 'Saving' : 'Save' }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { SolutionService } from '@/scripts/services/solution/solution-service';
import { differenceBy } from 'lodash';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SelectorTable = () => import('@/components/selector-table/selector-table');

export default {
    name: 'vendor-solution-publish-widget',

    components: {
        'app-button': AppButton,
        'selector-table': SelectorTable,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            clients: [],
            selectedClients: [],
            tableConfig: [
                {
                    id: 1,
                    cellWidth: '570px',
                    label: 'Name',
                    type: 'name',
                },
            ],
        };
    },

    computed: {
        agencyId() {
            return this.$store.state.context.agency.id;
        },

        availableClients() {
            return differenceBy(this.clients, this.selectedClients, 'id');
        },

        solutionId() {
            return this.widget.options.solutionId;
        },
    },

    created() {
        this.isLoading = true;
        this.getData()
            .then(this.getInitialSelected)
            .finally(() => (this.isLoading = false));
    },

    methods: {
        handleQuery(options) {
            this.isLoading = true;
            this.getData(options).finally(() => (this.isLoading = false));
        },

        mapItems(item) {
            return {
                id: item.Id,
                name: item.Name,
                thumbnailImageUrl: item.LogoImageUrl,
            };
        },

        getData(options = {}) {
            return new Promise((resolve, reject) => {
                const config = {
                    filter: `AgencyClients/any(ac: ac/AgencyId eq ${this.agencyId} and ac/IsAccepted eq true)`,
                    select: 'Id,Name,LogoImageUrl',
                };

                if (options.search) {
                    config.filter = `${config.filter} and contains(Name, '${options.search}')`;
                }

                SolutionService.getClients(config)
                    .then(({ data: { value } }) => {
                        this.clients = value.map(this.mapItems);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your partners.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getInitialSelected() {
            return new Promise((resolve, reject) => {
                const config = {
                    expand: 'AgencyClient($select=ClientId)',
                    select: 'AgencyClient',
                };

                SolutionService.getSharedAssets(this.solutionId, config)
                    .then(({ data: { value } }) => {
                        this.selectedClients = value.map((item) => {
                            return this.clients.find(({ id }) => id === item.AgencyClient.ClientId);
                        });
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your partners.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        handleRemovedItem(item) {
            this.selectedClients = this.selectedClients.filter(({ id }) => id !== item.id);
        },

        handleSelectedItem(item) {
            if (this.selectedClients.every(({ id }) => id !== item.id)) {
                this.selectedClients.push(item);
            }
        },

        handleToggleSelectAllItems(items) {
            if (this.clients.length === 0) {
                return (this.selectedClients = []);
            }
            items.map(this.handleSelectedItem);
        },

        save() {
            this.isLoading = true;
            const clientIds = this.selectedClients.map(({ id }) => id);

            SolutionService.publish(this.solutionId, clientIds)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your publish settings have been updated',
                    );
                    EventBus.$emit('solution:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.helpers.showFailNotification(
                        'An error occurred while saving your solution publish settings',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.vendor-solution-publish-widget .saving-step {
    text-align: center;
}

.vendor-solution-publish-widget .content-wrapper {
    padding: 20px;
}

.vendor-solution-publish-widget .content-wrapper .items-table .app-logo .background-image {
    border-radius: 50%;
}

.vendor-solution-publish-widget .action-container {
    display: flex;
}

.vendor-solution-publish-widget .action-container .btn {
    margin-left: 15px;
}

.vendor-solution-publish-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.vendor-solution-publish-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.vendor-solution-publish-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}
</style>
